/**
 * ログインページコンポーネント
 * レスポンシブ・ダークモード対応
 * 🛡️ セキュリティ強化：レート制限・セッション管理統合
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Center,
  Box,
  Badge,
  Group,
  Stack,
  Text,
  Loader,
  useMantineColorScheme,
  Button,
  Modal,
  Progress,
} from '@mantine/core';
import {
  IconCreditCard,
  IconCheck,
  IconShield,
  IconClock,
  IconLock,
} from '@tabler/icons-react';
import { LoginForm } from './LoginForm';
import { NFCLoginModal } from '@/shared/components/nfc/NFCLoginModal';
import { NFCCardIndicator } from '@/shared/components/nfc/NFCCardIndicator';
import axios from 'axios';
import { unifiedNFCService } from '@/services/nfc/unifiedNFCService';
import { showNotification } from '@mantine/notifications';
import { useNFC } from '@/features/nfc/contexts/NFCContext';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useNFCLoginWithoutNavigation } from '@/features/auth/api/hooks';
import { checkNFCAttempt, recordNFCFailure, recordNFCSuccess } from '@/features/auth/utils/rateLimiter';
import type { NFCLoginStep } from '@/shared/types';
import type { Staff } from '@/shared/types/staff';
import '@/../css/common.css';

export const LoginPage: React.FC = () => {
  const [showNFCModal, setShowNFCModal] = useState(false);
  const [detectedStaff, setDetectedStaff] = useState<Staff | null>(null);
  const [cardDetected, setCardDetected] = useState(false);
  const [nfcReading, setNfcReading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [nfcAuthSuccess, setNfcAuthSuccess] = useState(false);
  const [nfcStaffName, setNfcStaffName] = useState('');
  const [isNFCProcessing, setIsNFCProcessing] = useState(false);
  const [currentNFCStep, setCurrentNFCStep] = useState<NFCLoginStep>('detecting');
  const [nfcError, setNfcError] = useState<string | null>(null);
  
  // 🛡️ セキュリティ状態管理
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [securityInfo, setSecurityInfo] = useState<{
    type: 'rateLimit' | 'session';
    message: string;
    timeRemaining?: number;
  } | null>(null);
  
  const { isConnected } = useNFC();
  const authContext = useAuth();
  const nfcLoginMutation = useNFCLoginWithoutNavigation();
  
  // Null check for auth context
  useEffect(() => {
    if (!authContext) {
      showNotification({
        title: 'システムエラー',
        message: '認証システムが初期化されていません',
        color: 'red',
      });
    }
  }, [authContext]);

  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  // 🛡️ セッション情報の監視
  useEffect(() => {
    if (!authContext) return;

    const checkSessionStatus = () => {
      const sessionInfo = authContext.getSessionInfo();
      
      // セッションが非アクティブの場合は何もしない（ログイン画面なので正常）
      if (!sessionInfo.isActive) return;

      // 警告時間（5分前）の表示
      if (sessionInfo.timeUntilWarning < 60000 && sessionInfo.timeUntilWarning > 0) {
        const minutesRemaining = Math.ceil(sessionInfo.timeUntilWarning / 60000);
        setSecurityInfo({
          type: 'session',
          message: `${minutesRemaining}分後にセッションがタイムアウトします`,
          timeRemaining: sessionInfo.timeUntilWarning,
        });
        setShowSecurityModal(true);
      }
    };

    const interval = setInterval(checkSessionStatus, 30000); // 30秒ごとにチェック
    return () => clearInterval(interval);
  }, [authContext]);

  // NFCカード検出リスナー
  useEffect(() => {
    if (!isConnected) return;
    if (!authContext) {
      return;
    }

    const unsubscribe = unifiedNFCService.on('nfc_detected', async (event) => {
      
      // 書き込みモード中は無視
      if (event.context === 'write') return;
      
      // すでに処理中の場合は無視（重複防止）
      if (isNFCProcessing) {
        return;
      }

      const { uid, type } = event.data || {};

      // 🛡️ NFCレート制限チェック
      const rateLimitCheck = checkNFCAttempt(uid);
      if (!rateLimitCheck.allowed) {
        const errorMessage = rateLimitCheck.lockoutUntil 
          ? `NFCアクセスが制限されています。${new Date(rateLimitCheck.lockoutUntil).toLocaleTimeString()}まで待機してください`
          : 'NFCアクセスが一時的に制限されています';

        setSecurityInfo({
          type: 'rateLimit',
          message: errorMessage,
          timeRemaining: rateLimitCheck.lockoutUntil ? rateLimitCheck.lockoutUntil - Date.now() : undefined,
        });
        setShowSecurityModal(true);

        showNotification({
          title: 'NFCアクセス制限',
          message: errorMessage,
          color: 'orange',
          icon: <IconLock size={16} />,
          autoClose: 8000,
        });
        return;
      }

      // カード検出アニメーション
      console.log('[NFC] Card detected - showing pulse animation');
      setCardDetected(true);
      setCurrentNFCStep('detecting');
      setNfcError(null);
      setTimeout(() => setCardDetected(false), 2000);

      // スタッフカード（type=1）または未初期化カード（type=0）の場合処理
      if (type === 1 || type === 0) {
        // 処理開始フラグを立てる
        setIsNFCProcessing(true);
        // 読み取り開始
        setNfcReading(true);
        setCurrentNFCStep('detecting');

        // NFCログイン処理（TanStack Query使用に移行）
        (async () => {
          try {
            // 読み取りアニメーションを少し待つ
            await new Promise(resolve => setTimeout(resolve, 500));
            setNfcReading(false);
            setCurrentNFCStep('authenticating');
            
            // TanStack QueryでNFCログイン実行
            nfcLoginMutation.mutate({
              nfc_uid: uid,
              device_name: 'web',
            }, {
              onSuccess: (response) => {
                // 🛡️ NFCログイン成功記録
                recordNFCSuccess(uid);
                
                const staff = response.data.staff || response.data.user;
                setDetectedStaff(staff);
                
                // パルスアニメーション表示（2秒間）
                console.log('[NFC] Card detected - showing pulse animation');
                setTimeout(() => {
                  console.log('[NFC] Showing modal after 2 seconds');
                  setShowNFCModal(true);
                  // モーダル表示後、少し待ってからログイン処理を開始
                  setTimeout(() => {
                    console.log('[NFC] Starting login process');
                    handleNFCLogin(response.data.token, staff);
                  }, 500);
                }, 2000);
              },
              onError: (error: any) => {
                // 🛡️ NFCログイン失敗記録
                recordNFCFailure(uid);
                
                setCardDetected(false);
                setIsNFCProcessing(false);
                setCurrentNFCStep('error');
                
                // エラー情報の設定
                if (error?.response?.status === 422) {
                  setNfcError('登録されていないNFCカードです。管理者にお問い合わせください');
                  setSecurityInfo({
                    type: 'rateLimit',
                    message: '登録されていないNFCカードです。管理者にお問い合わせください',
                  });
                  setShowSecurityModal(true);
                } else {
                  setNfcError('NFCログインに失敗しました。再度お試しください');
                }
                // エラー通知はhooks内で処理済み
              }
            });
          } catch (error) {
            // 🛡️ NFCログイン失敗記録
            recordNFCFailure(uid);
            
            setNfcReading(false);
            setCardDetected(false);
            setIsNFCProcessing(false);
            setCurrentNFCStep('error');
            setNfcError('NFC処理中にエラーが発生しました');
            
            showNotification({
              title: 'エラー',
              message: 'NFC処理中にエラーが発生しました',
              color: 'red',
            });
          } finally {
            // タイムアウト対策として10秒後に強制リセット
            setTimeout(() => {
              if (isNFCProcessing) {
                setIsNFCProcessing(false);
                setNfcReading(false);
                setCardDetected(false);
                setCurrentNFCStep('error');
                setNfcError('処理がタイムアウトしました');
              }
            }, 10000);
          }
        })();
      } else if (type === 2) {
        // 顧客カードの場合
        showNotification({
          title: 'カードタイプエラー',
          message: 'スタッフカードをかざしてください',
          color: 'orange',
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isConnected, authContext, nfcLoginMutation]);

  const handleNFCLogin = async (token: string, staff: Staff) => {
    // Step 1: 認証処理中の表示
    // モーダルが表示された後、少し待ってから認証ステップに移行
    await new Promise((resolve) => setTimeout(resolve, 500));
    setCurrentNFCStep('authenticating');
    
    // ログイン検証中の表示を1.5秒
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Step 2: ログイン成功状態
    setCurrentNFCStep('success');
    setLoginSuccess(true);
    setNfcAuthSuccess(true);
    setNfcStaffName(staff.name);
    
    // AuthContextを通じて認証状態を更新
    if (authContext && authContext.nfcLogin) {
      authContext.nfcLogin(token, staff);
    } else {
      // Fallback: 直接localStorageとaxiosを設定
      localStorage.setItem('auth_token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      showNotification({
        title: '警告',
        message: '認証コンテキストが利用できません',
        color: 'orange',
      });
    }

    // ログイン成功通知はhooks側で処理されるため、ここでは削除
    // NFCLoginのhooksで自動的に通知が表示される

    // 成功表示を1.5秒間見せる
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // フラグをリセットしてからモーダルを閉じる
    setIsNFCProcessing(false);
    setShowNFCModal(false);
    
    // 画面遷移（React Routerを使用）
    console.log('[NFC] Navigating to dashboard');
    setTimeout(() => {
      navigate('/dashboard');
    }, 300);
  };

  // 🛡️ セッション延長処理
  const handleExtendSession = () => {
    if (authContext) {
      authContext.extendSession();
    }
    setShowSecurityModal(false);
    setSecurityInfo(null);
  };

  // 🛡️ セキュリティモーダルの自動クローズ処理
  useEffect(() => {
    if (!securityInfo?.timeRemaining) return;

    const interval = setInterval(() => {
      setSecurityInfo(prev => {
        if (!prev?.timeRemaining) return prev;
        const newTimeRemaining = prev.timeRemaining - 1000;
        if (newTimeRemaining <= 0) {
          setShowSecurityModal(false);
          return null;
        }
        return { ...prev, timeRemaining: newTimeRemaining };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [securityInfo?.timeRemaining]);

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: isDark 
          ? 'linear-gradient(135deg, #1a1b1e 0%, #25262b 100%)'
          : 'linear-gradient(135deg, #f5f7fa 0%, rgb(215, 230, 235) 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        position: 'relative',
      }}
    >
      <Container 
        size="xs"
        style={{
          width: '100%',
          maxWidth: '500px',
          padding: 0,
        }}
      >
        {/* NFCカード検出インジケーター - Custom Implementation */}
        {(cardDetected || nfcReading) && (
          <Box
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              background: isDark 
                ? 'rgba(37, 38, 43, 0.95)' 
                : 'rgba(255, 255, 255, 0.95)',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
              minWidth: '280px',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Stack align="center" gap="md">
              {nfcReading ? (
                <>
                  <Loader size="lg" color="blue" />
                  <Text size="sm" fw={500}>
                    カード情報を読み取っています...
                  </Text>
                </>
              ) : (
                <>
                  <Box
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  >
                    <IconCreditCard size={30} color="white" />
                  </Box>
                  <Text size="sm" fw={500} c="green">
                    NFCカードを検出しました
                  </Text>
                </>
              )}
            </Stack>
          </Box>
        )}

        {/* ログインフォーム */}
        <LoginForm />

        {/* NFCカード案内 */}
        {isConnected && (
          <Center 
            mt="lg"
          >
            <Badge
              size="lg"
              variant={
                nfcAuthSuccess || (!cardDetected && !nfcReading)
                  ? 'gradient'
                  : 'light'
              }
              gradient={
                nfcAuthSuccess
                  ? { from: 'lime', to: 'green', deg: 45 }
                  : (!cardDetected && !nfcReading)
                  ? { from: 'teal', to: 'lime', deg: 90 }
                  : { from: 'blue', to: 'cyan', deg: 45 }
              }
              color={
                cardDetected
                  ? 'violet'
                  : nfcReading
                  ? 'blue'
                  : 'teal'
              }
              leftSection={
                nfcAuthSuccess ? <IconCheck size={16} /> : <IconCreditCard size={16} />
              }
              styles={{
                root: {
                  transition: 'all 0.3s ease',
                  boxShadow: nfcAuthSuccess || (!cardDetected && !nfcReading)
                    ? '0 2px 8px rgba(0, 200, 83, 0.3)'
                    : 'none',
                },
              }}
            >
              {nfcAuthSuccess
                ? `${nfcStaffName}さん認証済み`
                : nfcReading
                ? 'カード読み取り中...'
                : 'NFCカードでもログイン可能'}
            </Badge>
          </Center>
        )}

        {/* NFCログインモーダル */}
        <NFCLoginModal
          opened={showNFCModal}
          onClose={() => {
            if (!isNFCProcessing && currentNFCStep !== 'success') {
              setShowNFCModal(false);
              setDetectedStaff(null);
              setLoginSuccess(false);
              setIsNFCProcessing(false);
              setCurrentNFCStep('detecting');
              setNfcError(null);
            }
          }}
          staff={detectedStaff}
          isProcessing={isNFCProcessing}
          loginSuccess={loginSuccess}
          currentStep={currentNFCStep}
          error={nfcError}
        />

        {/* 🛡️ セキュリティ警告モーダル */}
        <Modal
          opened={showSecurityModal}
          onClose={() => setShowSecurityModal(false)}
          title={
            <Group>
              <IconShield size={20} />
              <Text fw={600}>セキュリティ通知</Text>
            </Group>
          }
          size="md"
          centered
          overlayProps={{
            backgroundOpacity: 0.7,
            blur: 4,
          }}
        >
          <Stack>
            <Text size="sm" c="dimmed">
              {securityInfo?.message}
            </Text>

            {securityInfo?.timeRemaining && securityInfo.timeRemaining > 0 && (
              <Box>
                <Group justify="space-between" mb="xs">
                  <Text size="xs" c="dimmed">残り時間</Text>
                  <Text size="xs" fw={500}>
                    {Math.ceil(securityInfo.timeRemaining / 1000)}秒
                  </Text>
                </Group>
                <Progress 
                  value={100} 
                  color={securityInfo.type === 'session' ? 'yellow' : 'red'}
                  size="sm"
                  animated
                />
              </Box>
            )}

            <Group justify="flex-end" mt="md">
              {securityInfo?.type === 'session' && (
                <Button
                  leftSection={<IconClock size={16} />}
                  variant="gradient"
                  gradient={{ from: 'teal', to: 'lime' }}
                  onClick={handleExtendSession}
                >
                  セッション延長
                </Button>
              )}
              <Button
                variant="light"
                color="gray"
                onClick={() => setShowSecurityModal(false)}
              >
                閉じる
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </Box>
  );
};